import axios from 'axios';
import {
    HttpTransportType,
    type HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    type ISubscription,
} from '@microsoft/signalr';
import { Repeater } from '@repeaterjs/repeater';
import ItineraryPackageLoader, {
    type ItineraryPackageLoaderResult,
} from '../ItineraryPackageLoader';
import HotelPackage from './HotelPackage';
import { type HotelPackageLoaderConfig } from './types';
import { RequestCanceledException } from '@utils/itinerary';
import {
    type HotelFilterData,
    type HotelSearchHotelTypeViewCumulated,
} from '@itinerary/hotel/HotelModuleTypes';
import {
    type HotelFiltersResponse,
    HotelSearchApiApiHotelsGetHotelsFiltersPost,
    HotelSearchApiApiHotelsGetHotelsPricesPost,
    HotelSearchApiApiHotelsSearchGet,
    HotelSearchApiApiHotelsSearchstaticsGet,
    type HotelSearchResponse,
} from '@clients/HotelApi';

/**
 * The result from _getPackages method of HotelPackageLoader
 */
export interface HotelPackageLoaderResult
    extends ItineraryPackageLoaderResult<HotelPackage> {
    list: HotelPackage[];
    filterMetaData: HotelFilterData;
    totalCount: number;
}

export default class HotelPackageLoader extends ItineraryPackageLoader<HotelPackage> {
    private readonly _cityName: string;
    private readonly _adultCount: number;
    private readonly _childCount: number;
    private readonly _roomCount: number;
    private readonly _fromDate: string;
    private readonly _toDate: string;
    private readonly _static: boolean;
    private readonly _token?: string;
    private readonly _stars?: number;
    private readonly _xplayerID?: string;
    private hubConnection: HubConnection | null = null;
    private subscribe: ISubscription<any> | null = null;

    static revive(from: HotelPackageLoader): HotelPackageLoader {
        return Object.assign(
            new HotelPackageLoader({
                adultCount: 0,
                roomCount: 1,
                fromDate: '',
                static: false,
                token: '',
                cityName: '',
                childCount: 0,
                toDate: '',
            }),
            from
        );
    }

    constructor({
        cityName,
        adultCount,
        childCount,
        roomCount,
        fromDate,
        toDate,
        url,
        token,
        xplayerID,
        static: _static,
        stars,
    }: HotelPackageLoaderConfig) {
        super({ url });
        this._cityName = cityName;
        this._adultCount = adultCount;
        this._childCount = childCount;
        this._roomCount = roomCount;
        this._fromDate = fromDate;
        this._toDate = toDate;
        this._static = _static;
        this._token = token;
        this._xplayerID = xplayerID;
        this._stars = stars;
    }

    override get isRealTime() {
        return true;
    }

    override stopLoading(): void {
        this.subscribe?.dispose();
        this.hubConnection?.stop();
    }

    override get date() {
        return new Date(this._fromDate);
    }

    override compareTo(other: HotelPackageLoaderConfig): boolean {
        if (this._cityName !== other.cityName) {
            return false;
        }
        if (this._adultCount !== other.adultCount) {
            return false;
        }
        if (this._childCount !== other.childCount) {
            return false;
        }
        if (this._roomCount !== other.roomCount) {
            return false;
        }
        if (this._fromDate.toString() !== other.fromDate.toString()) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (this._toDate.toString() !== other.toDate.toString()) {
            return false;
        }
        return true;
    }

    async loadMore(pageNumber: number, axiosParams?: any) {
        const { data } = await HotelSearchApiApiHotelsSearchstaticsGet(
            {
                adultCount: 1, // this._adultCount,
                childrenCount: 0, // this._childCount,
                roomCount: this._roomCount,
                cityName: this._cityName,
                hasHotelInName: true,
                stars: this._stars,
                pageNumber,
            },
            axiosParams
        );
        return this.mapResponse(data);
    }

    async loadFilterMetadata(
        hotelIds: number[]
    ): Promise<Omit<HotelFilterData, 'maxPrice' | 'minPrice'>> {
        if (!this._static) {
            throw new Error('Filters already loaded');
        }
        const { data } = await HotelSearchApiApiHotelsGetHotelsFiltersPost({
            hotelFiltersRequest: { hotelIds },
        });
        return this.mapMetadata(data);
    }

    async loadPrices(hotelIds: number[]) {
        if (!this._static) {
            throw new Error('Prices already loaded');
        }
        const { data } = await HotelSearchApiApiHotelsGetHotelsPricesPost({
            hotelPricesRequest: { hotelIds },
        });
        return data.reduce(
            (prev, currentValue) => ({
                ...prev,
                [currentValue.hotelId ?? 0]: currentValue.price ?? 0,
            }),
            {} as Record<number, number>
        );
    }

    private mapMetadata(
        response: HotelFiltersResponse
    ): Omit<HotelFilterData, 'maxPrice' | 'minPrice'> {
        return {
            accommodationTypes: (
                response.hotelTypes?.map(type => ({
                    ...type,
                    title: type.title?.replace(/ \d+ ستاره/, ''),
                })) ?? []
            ).reduce((previousValue, currentValue) => {
                const find = previousValue.find(
                    val => val.title === currentValue.title
                );
                if (find) {
                    find.cumulatedIds.push(currentValue.id ?? 0);
                    if (find.minPrice || currentValue.minPrice) {
                        find.minPrice = Math.min(
                            find.minPrice || Number.MAX_SAFE_INTEGER,
                            currentValue.minPrice || Number.MAX_SAFE_INTEGER
                        );
                    }
                    return previousValue;
                } else {
                    return [
                        ...previousValue,
                        {
                            ...currentValue,
                            cumulatedIds: [currentValue.id ?? 0],
                        },
                    ];
                }
            }, [] as HotelSearchHotelTypeViewCumulated[]),
            amenities: response.hotelAmenities ?? [],
            serviceLevels: response.serviceLevels ?? [],
            specialOffers: response.hotelSpecialOffers ?? [],
        };
    }

    override async *_getPackages(
        axiosParams?: any
    ): AsyncGenerator<HotelPackageLoaderResult> {
        try {
            if (this._static) {
                try {
                    const { data } =
                        await HotelSearchApiApiHotelsSearchstaticsGet(
                            {
                                adultCount: 1, // this._adultCount,
                                childrenCount: 0, // this._childCount,
                                roomCount: this._roomCount,
                                cityName: this._cityName,
                                hasHotelInName: true,
                                stars: this._stars,
                            },
                            axiosParams
                        );
                    yield this.mapResponse(data);
                    return;
                } catch (ex) {
                    if (axios.isCancel(ex)) {
                        throw new RequestCanceledException();
                    }
                    throw ex;
                }
            }
            if (
                !this.hubConnection ||
                this.hubConnection.state === HubConnectionState.Disconnected ||
                this.hubConnection.state === HubConnectionState.Disconnecting
            ) {
                this.hubConnection = new HubConnectionBuilder()
                    .withUrl(`${useBaseUrls().hotel}/hotelhub`, {
                        accessTokenFactory: () => this._token ?? '',
                        transport: HttpTransportType.LongPolling,
                        headers: {
                            'X-PlayerId': this._xplayerID ?? '',
                        },
                    })
                    .build();
                await this.hubConnection.start();
            }
            const hubStream = this.hubConnection.stream('GetAvailable', {
                adultCount: 1, // this._adultCount,
                childrenCount: 0, // this._childCount,
                roomCount: this._roomCount,
                from: dayConfig.gregory(this._fromDate).format(formatter()),
                to: dayConfig.gregory(this._toDate).format(formatter()),
                cityName: this._cityName,
                hasHotelInName: true,
                'X-PlayerID': this._xplayerID ?? '',
            });
            const responses = new Repeater<HotelSearchResponse>(
                async (push, stop) => {
                    this.subscribe = hubStream.subscribe({
                        next: (res: HotelSearchResponse) => {
                            push(res);
                        },
                        complete: () => {
                            this.subscribe?.dispose();
                            stop();
                        },
                        error: (err: any) => {
                            this.subscribe?.dispose();
                            this.hubConnection?.stop();
                            stop(new Error(err));
                        },
                    });
                    await stop;
                    this.subscribe?.dispose();
                    this.hubConnection?.stop();
                }
            );
            for await (const response of responses) {
                yield this.mapResponse(response);
            }
        } catch (error) {
            try {
                const { data } = await HotelSearchApiApiHotelsSearchGet(
                    {
                        adultCount: 1, // this._adultCount,
                        childrenCount: 0, // this._childCount,
                        roomCount: this._roomCount,
                        from: dayConfig
                            .gregory(this._fromDate)
                            .format(formatter()),
                        to: dayConfig.gregory(this._toDate).format(formatter()),
                        cityName: this._cityName,
                        hasHotelInName: true,
                    },
                    axiosParams
                );
                yield this.mapResponse(data);
            } catch (ex) {
                if (axios.isCancel(ex)) {
                    throw new RequestCanceledException();
                }
                throw ex;
            }
        }
    }

    private mapResponse(response: HotelSearchResponse) {
        const hotels = response.hotels ?? [];
        return {
            list: hotels.map(
                hotel =>
                    new HotelPackage({
                        hotel,
                        fromDate: this._fromDate,
                        toDate: this._toDate,
                        isStatic: this._static,
                    })
            ),
            filterMetaData: {
                maxPrice: response.maxPrice ?? 0,
                minPrice: response.minPrice ?? 0,
                ...this.mapMetadata(response),
            },
            totalCount: response.totalCountHotels ?? 0,
        };
    }

    setLastLoaded() {
        this.lastLoadTime = new Date();
    }
}
